import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KEY = "dd_cart_v1";
const MAX_QTY = 5;
const CartContext = createContext(null);

const read = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) || "{}");
    return Array.isArray(raw.items) ? raw.items : [];
  } catch {
    return [];
  }
};

export const lineKey = (i) => `${i.lookupKey}|${i.size || ""}|${i.color || ""}`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(read);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify({ items }));
  }, [items]);

  const add = useCallback((line) => {
    setItems((list) => {
      const k = lineKey(line);
      const existing = list.find((i) => lineKey(i) === k);
      if (existing) return list.map((i) => (lineKey(i) === k ? { ...i, quantity: Math.min(MAX_QTY, i.quantity + (line.quantity || 1)) } : i));
      return [...list, { ...line, quantity: Math.min(MAX_QTY, line.quantity || 1), addedAt: Date.now() }];
    });
  }, []);

  const setQuantity = useCallback((k, quantity) => {
    setItems((list) => (quantity <= 0 ? list.filter((i) => lineKey(i) !== k) : list.map((i) => (lineKey(i) === k ? { ...i, quantity: Math.min(MAX_QTY, quantity) } : i))));
  }, []);

  const remove = useCallback((k) => setItems((list) => list.filter((i) => lineKey(i) !== k)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => {
    const count = items.reduce((s, i) => s + i.quantity, 0);
    const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
    return { items, count, subtotal, add, setQuantity, remove, clear, open, setOpen };
  }, [items, add, setQuantity, remove, clear, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
